import { action, makeObservable, observable, runInAction } from 'mobx'
import socketClient, { Socket } from 'socket.io-client'

import ChatEvent from './events'
import { IMessageReceivedEvent } from './types'

const chatBase = '/chat-service'

export default class ChatService {
  @observable public allUnreadMessagesCount: number = 0

  public socket: Socket = null
  public userId: string = null

  public constructor(
    private readonly getValidAccessToken: () => Promise<string>,
  ) {
    makeObservable(this)
  }

  public async init(userId: string) {
    this.userId = userId

    this.socket = socketClient('', {
      path: `${chatBase}/socket.io/`,
      query: { userId },
      auth: async cb => {
        // Degrade gracefully on a refresh miss. getValidAccessToken() can
        // reject — most often the concurrent-refresh race-loser 401
        // (STX1-616 / STRUXHUBDESKTOP-5SD), which the auth service now
        // recovers from, but a genuinely-expired session still throws here.
        // Without this guard the rejection is unhandled (Socket.IO does not
        // await the auth callback), surfacing in Sentry via
        // onunhandledrejection and riding the global logout. Hand the socket a
        // null token instead: the server rejects it and Socket.IO emits
        // connect_error, which we already log and which retries on the next
        // reconnect once auth recovers.
        try {
          cb({ token: await this.getValidAccessToken() })
        } catch (err) {
          console.warn(
            `Chat socket auth: could not obtain access token (${
              err instanceof Error ? err.message : err
            }); connecting without one`,
          )
          cb({ token: null })
        }
      },
    })

    this.socket.on(ChatEvent.ConnectError, err => {
      console.warn(`Chat [${ChatEvent.ConnectError}] due to ${err.message}`)
    })

    this.socket.on(
      ChatEvent.MessageReceived,
      ({ message }: IMessageReceivedEvent) => {
        if (message.authorId !== this.userId) {
          this.allUnreadMessagesCount++
        }
      },
    )

    this.allUnreadMessagesCount = await this.getAllUnreadMessagesCount()
  }

  public async getAllUnreadMessagesCount(): Promise<number> {
    const res = await this.fetchChatApi(`messages/unread-count/${this.userId}`)
    return res.ok ? Number(await res.json()) : 0
  }

  public async getOnlineContacts(): Promise<string[]> {
    const res = await this.fetchChatApi('online-contacts')
    return res.ok ? res.json() : []
  }

  @action.bound
  public async refreshAllUnreadMessagesCount(): Promise<void> {
    const count = await this.getAllUnreadMessagesCount()
    runInAction(() => {
      this.allUnreadMessagesCount = count
    })
  }

  public disconnect() {
    this.socket?.close()
  }

  private async fetchChatApi(
    route: string,
    method: string = 'GET',
    headers = {},
    body = undefined,
  ): Promise<Response> {
    const accessToken = await this.getValidAccessToken()

    return fetch(`${chatBase}/${route}`, {
      method,
      body,
      headers: { ...headers, authorization: `Bearer ${accessToken}` },
    })
  }
}
